/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { getSpansForElement } from '../svgTextDisplay/getSpansForElement';
import { generatePatternStyles } from '@web-stories-wp/patterns';
import { getTextContentWidth } from './textBoxMetrics';
import { generateFontFamily } from './generateFontFamily';
import { GRADIENT_PATTERN_STYLE_PROP } from '../../../utils/textGradient';
import { getTextLineGeometry } from './textLineGeometry';

export function getMeasuredLineBreakContent({
  element,
  dataToEditorX = (value) => value,
  dataToEditorY = (value) => value,
  preserveColor = false,
  fontSizeToCss,
  fontSizeAttributeScale = 1,
  lineBandHeightCss,
  lineHeightCss,
  materializeFontSizes = false,
  materializeLineHeights = false,
  verticalPadding = 0,
  wrapLines = false,
  width,
}) {
  if (typeof document === 'undefined' || typeof DOMParser === 'undefined') {
    return null;
  }

  const spans = getSpansForElement({
    dataToEditorX,
    dataToEditorY,
    text: element,
    width: getTextContentWidth({
      element,
      dataToEditorX,
      width: width ?? dataToEditorX(element.width),
    }),
  });

  if (spans.length === 0) {
    return null;
  }

  const spanMarkupOptions = {
    preserveColor,
    fontSizeToCss:
      fontSizeToCss || ((fontSize) => `${dataToEditorY(fontSize)}px`),
    fontSizeAttributeScale,
    lineHeightCss: wrapLines ? undefined : lineHeightCss,
    materializeFontSizes,
    materializeLineHeights,
  };

  if (wrapLines) {
    return getMeasuredLineMarkup(spans, {
      ...spanMarkupOptions,
      dataToEditorY,
      defaultFontSize: element.fontSize,
      element,
      lineBandHeightCss,
      lineHeightCss,
      verticalPadding,
    });
  }

  let currentLineNumber = spans[0].lineNumber;
  let output = '';

  for (const span of spans) {
    while (currentLineNumber < span.lineNumber) {
      output += '<br/>';
      currentLineNumber++;
    }

    output += getMeasuredSpanMarkup(span, spanMarkupOptions);
  }

  return output;
}

function getMeasuredLineMarkup(
  spans,
  {
    defaultFontSize,
    dataToEditorY,
    element,
    lineBandHeightCss,
    lineHeightCss,
    fontSizeToCss,
    verticalPadding,
    ...spanMarkupOptions
  }
) {
  const firstLineNumber = spans[0].lineNumber;
  const lines = [];
  const geometries = new Map(
    getTextLineGeometry({
      spans: spans.map((span) => ({
        lineNumber: span.lineNumber,
        font: span.font,
        fontSize: span.fontSize,
        fontSizeOverride: span.fontSizeOverride,
        lineHeight: span.lineHeight,
        lineHeightOverride: span.lineHeightOverride,
      })),
      defaultFont: element.font,
      defaultFontSize: dataToEditorY(element.fontSize),
      lineHeight: element.lineHeight,
      paddingBetweenLines: verticalPadding,
    }).map((geometry) => [geometry.lineNumber, geometry])
  );

  for (const span of spans) {
    const lineIndex = span.lineNumber - firstLineNumber;
    while (lines.length <= lineIndex) {
      lines.push([]);
    }
    lines[lineIndex].push(span);
  }

  return lines
    .map((lineSpans) => {
      const isEmptyLine =
        lineSpans.length === 0 ||
        lineSpans.every((span) => span.content.length === 0);
      const maximumFontSize = isEmptyLine
        ? defaultFontSize
        : Math.max(...lineSpans.map((span) => span.fontSizeInDataUnits));
      const usesInlineLineHeight = lineSpans.some(
        (span) => span.lineHeightOverride !== undefined
      );
      const geometry = usesInlineLineHeight
        ? geometries.get(lineSpans[0]?.lineNumber)
        : undefined;
      const effectiveLineHeightCss = geometry
        ? `${geometry.height + verticalPadding * 2}px`
        : lineHeightCss;
      const styles = [`font-size: ${fontSizeToCss(maximumFontSize)}`];
      if (effectiveLineHeightCss) {
        styles.push(`height: ${effectiveLineHeightCss}`);
        styles.push(`line-height: ${effectiveLineHeightCss}`);
      }
      if (isEmptyLine) {
        return `<span data-st-text-line data-st-empty-text-line style="${escapeHtmlAttribute(
          styles.join('; ')
        )}"></span>`;
      }

      const content = lineSpans
        .map((span) =>
          getMeasuredSpanMarkup(span, {
            ...spanMarkupOptions,
            fontSizeToCss,
          })
        )
        .join('');

      const bandStyle = lineBandHeightCss
        ? ` style="${escapeHtmlAttribute(`height: ${lineBandHeightCss}`)}"`
        : '';

      return `<span data-st-text-line style="${escapeHtmlAttribute(
        styles.join('; ')
      )}"><span data-st-text-line-band${bandStyle}>${content}</span></span>`;
    })
    .join('<br/>');
}

export function stripColorStylesFromMeasuredLineBreakContent(content) {
  if (!content || typeof DOMParser === 'undefined') {
    return content;
  }

  const { body } = new DOMParser().parseFromString(content, 'text/html');

  body.querySelectorAll('[style]').forEach((element) => {
    element.style.removeProperty('color');

    if (!element.getAttribute('style')) {
      element.removeAttribute('style');
    }
  });

  return body.innerHTML;
}

function getMeasuredSpanMarkup(
  span,
  {
    preserveColor,
    fontSizeToCss,
    fontSizeAttributeScale,
    lineHeightCss,
    materializeFontSizes,
    materializeLineHeights,
  }
) {
  const styles = [];
  const attributes = [];

  if (span.fontReferenceId) {
    attributes.push(
      `data-st-font-ref="${escapeHtmlAttribute(span.fontReferenceId)}"`
    );
    styles.push(
      `font-family: ${generateFontFamily(span.font, span.renderFamily)}`
    );
  }
  if (span.fontSizeOverride !== undefined) {
    attributes.push(
      `data-st-font-size="${Number(
        (span.fontSizeOverride / fontSizeAttributeScale).toFixed(6)
      )}"`
    );
  }
  if (span.fontSizeOverride !== undefined || materializeFontSizes) {
    styles.push(`font-size: ${fontSizeToCss(span.fontSizeInDataUnits)}`);
  }
  if (span.lineHeightOverride !== undefined) {
    attributes.push(
      `data-st-line-height="${Number(span.lineHeightOverride.toFixed(6))}"`
    );
  }
  if (span.lineHeightOverride !== undefined || materializeLineHeights) {
    styles.push(`line-height: ${span.lineHeight}`);
  } else if (lineHeightCss) {
    styles.push(`line-height: ${lineHeightCss}`);
  }

  if (preserveColor && span.gradient && span.gradientKey) {
    const { backgroundImage } = generatePatternStyles(
      { ...span.gradient, geometryVersion: undefined },
      'background'
    );
    styles.push(`${GRADIENT_PATTERN_STYLE_PROP}: ${span.gradientKey}`);
    if (backgroundImage) {
      styles.push(`background-image: ${backgroundImage}`);
    }
    styles.push('background-clip: text');
    styles.push('-webkit-background-clip: text');
    styles.push('-webkit-text-fill-color: transparent');
    if (span.color) {
      styles.push(`color: ${span.color}`);
    }
  } else if (preserveColor && span.color) {
    styles.push(`color: ${span.color}`);
  }
  if (span.fontWeight && span.fontWeight !== 400) {
    styles.push(`font-weight: ${span.fontWeight}`);
  }
  if (span.fontStyle && span.fontStyle !== 'normal') {
    styles.push(`font-style: ${span.fontStyle}`);
  }
  if (span.letterSpacing && span.letterSpacing !== '0em') {
    styles.push(`letter-spacing: ${span.letterSpacing}`);
  }
  if (span.textDecoration) {
    styles.push(`text-decoration: ${span.textDecoration}`);
  }
  if (span.textTransform) {
    styles.push(`text-transform: ${span.textTransform}`);
  }

  if (span.content === '' && span.lineHeightOverride !== undefined) {
    styles.push(
      'display: inline-block',
      'vertical-align: top',
      `font-size: ${fontSizeToCss(span.fontSizeInDataUnits)}`,
      `height: ${span.lineHeight}em`
    );
  }
  const styleAttribute = styles.length
    ? ` style="${escapeHtmlAttribute(styles.join('; '))}"`
    : '';
  const dataAttributes = attributes.length ? ` ${attributes.join(' ')}` : '';

  return `<span${dataAttributes}${styleAttribute}>${escapeHtmlText(
    span.content
  )}</span>`;
}

function escapeHtmlText(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtmlAttribute(value) {
  return escapeHtmlText(value).replace(/"/g, '&quot;');
}
